import { afterRenderEffect, Component, computed, ElementRef, inject, input, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { mutedMapTiles, Point, Rect } from './muted-map-tiles';

// Committed on purpose: key "galaxy-maps-embed-client" (GCP project map-for-work-ext-02145071) allows only the
// free Maps Embed API, which Google calls sufficient protection for a key shipped to browsers. Demo and prod share it.
const EMBED_API_KEY = 'AIzaSyBF-ulMDt14-9Ji4upE547-Bii_59oLAro';

const MUTED_COLOR_MATRIX = [
  [0.3382, 0.5065, -0.4214, 0, 0.5679],
  [0.1506, 0.3678, -0.1156, 0, 0.5913],
  [-0.0037, 0.2086, 0.1682, 0, 0.622],
  [0, 0, 0, 1, 0],
]
  .flat()
  .join(' ');

let nextMutedFilterId = 0;

export interface MapPoint {
  latitude: number;
  longitude: number;
}

/**
 * A non-interactive Google map with one pin, drawn by the free Maps Embed API. Size it by styling the host.
 * Draws nothing unless {@link hasCoordinates} accepts every point.
 */
@Component({
  selector: 'core-google-maps-embed',
  templateUrl: './google-maps-embed.component.html',
  styleUrl: './google-maps-embed.component.scss',
  host: {
    '[class.muted]': 'muted()',
    '[style.--muted-filter]': 'muted() ? mutedFilterUrl : null',
  },
})
export class GoogleMapsEmbedComponent {
  public readonly location = input.required<MapPoint>();
  public readonly zoom = input.required<number>();
  public readonly center = input<MapPoint>();
  /**
   * Recolours the map to the muted grey Business App style. Google's pin, logo and attribution keep their own
   * colours, because the Maps Platform terms forbid modifying attribution; this relies on where Google draws them.
   */
  public readonly muted = input(false);

  protected readonly mutedFilterId = `core-google-maps-embed-muted-${nextMutedFilterId++}`;
  protected readonly mutedFilterUrl = `url(#${this.mutedFilterId})`;
  protected readonly mutedColorMatrix = MUTED_COLOR_MATRIX;
  protected readonly mutedTiles = computed<Rect[]>(() => {
    const { width, height } = this.size();
    if (!this.muted() || !width || !height) {
      return [];
    }
    return mutedMapTiles(
      width,
      height,
      pinTipOffset(this.location(), this.center() ?? this.location(), this.zoom(), width, height),
    );
  });

  protected readonly src = computed<SafeResourceUrl | null>(() => {
    const location = this.location();
    const center = this.center();
    if (!hasCoordinates(location) || (center && !hasCoordinates(center))) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(buildEmbedUrl(location, this.zoom(), center));
  });

  private readonly sanitizer = inject(DomSanitizer);
  private readonly size = signal({ width: 0, height: 0 });

  constructor() {
    const host: HTMLElement = inject(ElementRef).nativeElement;
    afterRenderEffect((onCleanup) => {
      if (!this.muted()) {
        return;
      }
      const observer = new ResizeObserver(([entry]) =>
        this.size.set({ width: entry.contentRect.width, height: entry.contentRect.height }),
      );
      observer.observe(host);
      onCleanup(() => observer.disconnect());
    });
  }
}

function buildEmbedUrl(location: MapPoint, zoom: number, center: MapPoint | undefined): string {
  const params = new URLSearchParams({ key: EMBED_API_KEY, q: formatPoint(location), zoom: String(zoom) });
  if (center) {
    params.set('center', formatPoint(center));
  }
  return `https://www.google.com/maps/embed/v1/place?${params}`;
}

/**
 * Whether a point is a real location. The API omits a 0,0 location, so it arrives with undefined coordinates, and
 * Google reads an undefined coordinate as a text search that pins an unrelated place. An explicit 0,0 is treated as
 * unset too.
 */
export function hasCoordinates(point: Partial<MapPoint>): boolean {
  const { latitude, longitude } = point;
  return Number.isFinite(latitude) && Number.isFinite(longitude) && !(latitude === 0 && longitude === 0);
}

function pinTipOffset(location: MapPoint, center: MapPoint, zoom: number, width: number, height: number): Point {
  const pixelsPerDegree = (256 * 2 ** zoom) / 360;
  return {
    x: width / 2 + (location.longitude - center.longitude) * pixelsPerDegree,
    y: height / 2 - (mercatorLatitude(location.latitude) - mercatorLatitude(center.latitude)) * pixelsPerDegree,
  };
}

function mercatorLatitude(latitude: number): number {
  return (Math.log(Math.tan(Math.PI / 4 + (latitude * Math.PI) / 360)) * 180) / Math.PI;
}

function formatPoint(point: MapPoint): string {
  return `${point.latitude},${point.longitude}`;
}
